import AmrApi from 'Cloud/Application/Editor/MyOffice/amrApi.js';
import { downloadDocument, downloadDocumentMail } from 'Cloud/Application/Editor/MyOffice/helpers/downloadDocument';
import { getModeByRateLimit, rateLimitUpdate } from 'Cloud/Application/Editor/MyOffice/helpers/rateLimit';
import { saveDocument } from 'Cloud/Application/Editor/MyOffice/helpers/saveDocument';
import { sendMyofficeGa } from 'Cloud/Application/Editor/MyOffice/helpers/sendMyofficeGa';
import { MY_OFFICE_IFRAME, RATELIMIT_UPDATE_INTERVAL, SAVE_INTERVAL } from 'Cloud/Application/Editor/MyOffice/myOffice.constants';
import { logger } from 'lib/logger';
import xray from 'lib/xray';
import { sendKaktamLog } from 'reactApp/utils/ga';

let editorApi;
let fileInfo;

export const initAndLoadAmr = ({
    iframe,
    item,
    listeners,
    documentMode,
    isMyOfficeForMail,
    isEditAttaches,
}): undefined | (() => Promise<void>) => {
    if (!item) {
        return;
    }

    const { name, url, editors } = item;
    const { onLoad, onError } = listeners;

    let documentHasChanges = false;

    const onLoadIframe = async () => {
        AmrApi.setExperimentalFeaturesEnabled(true);

        editorApi = new AmrApi(iframe, MY_OFFICE_IFRAME);

        const myOfficeUrl = editors?.myoffice?.url;
        const { content, error } =
            isMyOfficeForMail && myOfficeUrl ? await downloadDocumentMail(myOfficeUrl) : await downloadDocument(url.get);

        if (!content) {
            logger.error('Не смогли скачать файл: ', error);

            xray.send('amr_down_file_err', {
                rlog: 'cloud_amr_file_err',
                rlog_message: {
                    type: 'download',
                    url,
                    error,
                },
            });

            onError();
            return;
        }

        const mode = await getModeByRateLimit(documentMode);

        const myOfficeParams = {
            lang: 'ru-RU',
            experimentalFeaturesEnabled: true,
            filename: name,
            mode,
            content,
        };

        editorApi.onError(({ errorInfo }) => {
            sendMyofficeGa('amr-error');
            logger.error('AMR error: ', errorInfo);

            xray.send('amr_on_err', {
                rlog: 'cloud_amr_file_err',
                rlog_message: {
                    type: 'on-error',
                    error: errorInfo,
                },
            });
        });

        editorApi.onPageReloadRequested(() => {
            sendMyofficeGa('amr-reload');
            window.location.reload();
        });

        editorApi.onChange(({ hasChanges }) => {
            sendMyofficeGa('amr-change');
            documentHasChanges = hasChanges;
        });

        try {
            fileInfo = await editorApi.openDocument(myOfficeParams);
        } catch (error) {
            logger.error('Ошибка при инициализации редактора', error);

            const suffix = isEditAttaches ? '-att-editor' : '';
            sendMyofficeGa(`init-error${suffix}`);
            sendKaktamLog({ error }, `myoffice-init-error${suffix}`);

            xray.send('amr_file_init_err', {
                rlog: 'cloud_amr_file_err',
                rlog_message: {
                    type: 'init',
                    error,
                },
            });

            onError();
            return;
        }

        logger.info('Документ готов к редактированию');

        const suffix = isEditAttaches ? '-att-editor' : '';
        sendMyofficeGa(`init-success${suffix}`);

        onLoad();

        const saveDocumentProcess = async () => {
            if (!documentHasChanges) {
                return;
            }

            try {
                const fileContent = await editorApi.saveDocument(fileInfo.format);
                const { error } = await saveDocument(item, fileContent);

                if (error) {
                    throw error;
                }
            } catch (error) {
                logger.error('Не смогли сохранить документ');
            }
        };

        setInterval(saveDocumentProcess, SAVE_INTERVAL);

        // Раз в 2 минуты ходим в /ratelim
        // @see https://confluence.mail.ru/pages/viewpage.action?pageId=701933833
        setInterval(() => rateLimitUpdate(mode), RATELIMIT_UPDATE_INTERVAL);
    };

    iframe.addEventListener('load', onLoadIframe);

    return onLoadIframe;
};
